import { z } from '@harvestiq/zod';
import { recipientSchema } from '../users/recipients';
import {
  LivestockInsuranceTypes,
  RmaLgmCommodityCodes,
  RmaLrpCommodityCodes,
} from '@harvestiq/constants';
import { zIsoDate } from '@harvestiq/zod';
import { lgmDetailsMarketingMonthsSchema } from './coverage-details';

export const coverageReminderLgmMarketingMonthSchema =
  lgmDetailsMarketingMonthsSchema.extend({
    formattedMarketingMonth: z.string(), // formatted month and year - MMM yyyy - e.g. November 2024
    numberOfHead: z.number(),
  });

export const coverageReminderDataSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(LivestockInsuranceTypes.LRP),
    coverageId: z.string(),
    salesEffectiveDate: zIsoDate(),
    assignedAgent: z
      .object({
        email: z.string(),
        name: z.string(),
        phone: z.string().optional(),
      })
      .optional(),
    coverageLevel: z.number(), // ?decimal?
    coveragePricePerCwt: z.number(), // ?decimal?
    coveragePricePerHead: z.number(), // ?decimal?
    targetWeight: z.coerce.number(),
    endDate: zIsoDate(),
    endorsementLength: z.coerce.number(), // can be string or number in DB
    numberOfHead: z.coerce.number(), // can be string or number in DB
    commodityCode: z.nativeEnum(RmaLrpCommodityCodes),
    commodityType: z.string(),
    insuredName: z.string(),
    isPre2026BeginningFarmerRancher: z.boolean().optional(),
    isVeteranFarmerRancher: z.boolean().optional(),
    beginningFarmerRancherCropYearCount: z.number().int().nullable().optional(),
  }),
  z.object({
    type: z.literal(LivestockInsuranceTypes.LGM),
    coverageId: z.string(),
    salesEffectiveDate: zIsoDate(),
    assignedAgent: z
      .object({
        email: z.string(),
        name: z.string(),
        phone: z.string().optional(),
      })
      .optional(),
    endDate: zIsoDate(),
    deductible: z.number(),
    marketingMonths: z.array(coverageReminderLgmMarketingMonthSchema),
    commodityCode: z.nativeEnum(RmaLgmCommodityCodes),
    commodityType: z.string(),
    liveCattleTargetCwtPerHead: z.number().nullable(),
    feederCattleTargetCwtPerHead: z.number().nullable(),
    cornTargetBuPerHead: z.number().nullable(),
    insuredName: z.string(),
    isPre2026BeginningFarmerRancher: z.boolean().optional(),
    isVeteranFarmerRancher: z.boolean().optional(),
    beginningFarmerRancherCropYearCount: z.number().int().nullable().optional(),
  }),
]);

export type CoverageReminderData = z.infer<typeof coverageReminderDataSchema>;

export const coverageReminderApiResponseSchema = z.object({
  coverages: z
    .object({
      to: z.array(recipientSchema),
      cc: z.array(recipientSchema).optional(),
      coverageReminderData: coverageReminderDataSchema,
    })
    .array(),
});

export type CoverageReminderApiResponse = z.infer<
  typeof coverageReminderApiResponseSchema
>;

export const coverageReminderApiRequestSchema = z.object({
  type: z.nativeEnum(LivestockInsuranceTypes),
  sendPreview: z.string().optional(),
  currentDay: zIsoDate().optional(),
});

export type CoverageReminderApiRequest = z.infer<
  typeof coverageReminderApiRequestSchema
>;
